import InsightsTooltip from 'components/ReusableComponents/Charts/Inslghts/InsightsTooltip'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { renderReferenceArea } from 'components/ReusableComponents/RestrictedDatesForCharts'
import Toggle from 'components/ReusableComponents/Toggle'
import renderYearSeperators from 'components/ReusableComponents/YearSeperatorsForCharts'
import { usePerformanceReportsDates } from 'features/Analytics'
import AnalyticTabs from 'features/Analytics/AnalyticTab'
import AnalyticsMaintenanceBar from 'features/Analytics/MaintenanceBar'
import SegmentsChart from 'features/Analytics/SegmentsChart'
import Diagnose from 'features/Diagnose'
import { useInternalAdmin } from 'hooks'
import useAllHotelsToggle from 'hooks/useAllHotels'
import useRestrictedDates from 'hooks/useRestrictedDates'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import performanceReportsApi from 'services/api/performanceReports'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import { provideEndDate } from '../provideEndDate'
import mapPreview from './map-preview.jpg'
import OTAIcon from './OTAIcon'
import styles from './Outreach.module.scss'

const Outreach = () => {
  const { fromDate, setFromDate, toDate, setToDate } = usePerformanceReportsDates()

  const {
    showAllHotels,
    setShowAllHotels,
    showWholeGroup,
    setShowWholeGroup,
    parentChain,
    setParentChain,
    idsOfCurrentGroup,
    idsOfAllHotels,
    isAllTime,
    setIsAllTime
  } = useAllHotelsToggle()

  const [campaignSummary, setCampaignSummary] = useState()
  const [scheduledMessageSummary, setScheduledMessageSummary] = useState()

  const [showGroupedCampaigns, setShowGroupedCampaigns] = useState(true)

  const [otaGuestsReached, setOtaGuestsReached] = useState()
  const [messageActivities, setmessageActivities] = useState()
  const [textBlastData, settextBlastData] = useState()

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)

  const restrictedDates = useRestrictedDates(fromDate, toDate)

  const isInternalAdmin = useInternalAdmin()

  const MAX_SEGMENTS_TO_SHOW = 7
  const [showingAllSegments, setShowingAllSegments] = useState(false)

  useEffect(() => {
    setIsLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    performanceReportsApi
      .outreach(
        {
          fromDate,
          toDate: provideEndDate(toDate),
          kind,
          parentChainCode: parentChain,
          isAllTheTime: isAllTime
        },
        abortController
      )
      .then(res => {
        setCampaignSummary(res.campaignSummary)
        setOtaGuestsReached(res?.otaSummary?.total)
        setScheduledMessageSummary(res.scheduledMessageSummary)
        setmessageActivities(res.messageActivities)
        settextBlastData(res.textBlastSummary)
        setIsErred(false)
        setIsLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setIsErred(true)
          setIsLoading(false)
        }
      })

    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain,
    isAllTime
  ])

  return (
    <div>
      <AnalyticTabs
        showWholeGroup={showWholeGroup}
        setShowWholeGroup={setShowWholeGroup}
        setShowAllHotels={setShowAllHotels}
        showAllHotels={showAllHotels}
        setFromDate={setFromDate}
        setToDate={setToDate}
        fromDate={fromDate}
        toDate={toDate}
        parentChain={parentChain}
        setParentChain={setParentChain}
        isAllTime={isAllTime}
        setIsAllTime={setIsAllTime}
      />
      <AnalyticsMaintenanceBar />
      <DataIsReprocessingBar />
      <Diagnose />
      {isErred && (
        <div className={styles.noResults}>
          <p>Something went wrong while getting outreach data</p>
        </div>
      )}
      {!isErred && (
        <div className={styles.wrapper}>
          <div className={styles.row}>
            <div className={styles.mapHolder}>
              <h3 className={styles.title}>Reached guests by location</h3>
              <img
                src={mapPreview}
                width={560}
                alt="map feature is coming soon"
                style={{ marginTop: 10, marginLeft: 15 }}
              />
            </div>
            <div className={styles.segmentsHolder}>
              <h3 className={styles.segmentsTitle}>Outreach by segments</h3>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <>
                  <div className={styles.segmentsHeader}>
                    <div className={styles.segmentStat}>
                      <p className={styles.segmentsCountNumber}>
                        {formatNumberAsThousands(campaignSummary?.total)}
                      </p>
                      <p className={styles.segmentsCountLabel}>guests reached in total</p>
                    </div>
                    {isInternalAdmin && (
                      <div className={styles.segmentStat}>
                        <p className={styles.segmentsCountNumber}>
                          {scheduledMessageSummary?.numberOfMessagesTotal
                            ? formatNumberAsThousands(
                                scheduledMessageSummary?.numberOfMessagesTotal
                              )
                            : 0}
                        </p>
                        <p className={styles.segmentsCountLabel}>Scheduled messages</p>
                      </div>
                    )}
                  </div>
                  {campaignSummary && (
                    <div className={styles.segmentsContent}>
                      <SegmentsChart
                        barData={campaignSummary?.[
                          showGroupedCampaigns ? 'activities' : 'activitiesDetails'
                        ]
                          .sort((a, b) => b.count - a.count)
                          .slice(0, MAX_SEGMENTS_TO_SHOW)}
                      />
                    </div>
                  )}
                  {(isInternalAdmin
                    ? true
                    : campaignSummary?.[
                        showGroupedCampaigns ? 'activities' : 'activitiesDetails'
                      ].length > MAX_SEGMENTS_TO_SHOW) && (
                    <div className={styles.segmentsFooter}>
                      {isInternalAdmin && (
                        <Toggle
                          label="Group campaigns"
                          className={styles.groupCampaignsToggle}
                          checked={showGroupedCampaigns}
                          handleChange={() =>
                            setShowGroupedCampaigns(!showGroupedCampaigns)
                          }
                        />
                      )}
                      {campaignSummary?.[
                        showGroupedCampaigns ? 'activities' : 'activitiesDetails'
                      ].length > MAX_SEGMENTS_TO_SHOW && (
                        <button
                          className={styles.moreSegmentsButton}
                          onClick={() => setShowingAllSegments(true)}
                        >
                          <span className={styles.desktopText}>
                            Show{' '}
                            {campaignSummary?.[
                              showGroupedCampaigns ? 'activities' : 'activitiesDetails'
                            ].length - MAX_SEGMENTS_TO_SHOW}{' '}
                            more segments
                          </span>
                          <span className={styles.mobileText}>Show more</span>
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
              {showingAllSegments && (
                <ConfirmationDialog
                  title="Outreach by segments"
                  onClose={() => setShowingAllSegments(false)}
                  maxWidth={700}
                  contentPadding={0}
                  bottomBarDetail={
                    isInternalAdmin && (
                      <Toggle
                        label="Group campaigns"
                        className={styles.groupCampaignsToggleWithinDialog}
                        checked={showGroupedCampaigns}
                        handleChange={() =>
                          setShowGroupedCampaigns(!showGroupedCampaigns)
                        }
                      />
                    )
                  }
                >
                  <div style={{ width: 650 }}>
                    {campaignSummary && (
                      <>
                        <SegmentsChart
                          barData={campaignSummary?.[
                            showGroupedCampaigns ? 'activities' : 'activitiesDetails'
                          ].sort((a, b) => b.count - a.count)}
                        />
                      </>
                    )}
                  </div>
                </ConfirmationDialog>
              )}
            </div>
          </div>
          <div className={styles.row}>
            <div>
              <div className={styles.otaHolder}>
                {isLoading && <LoadingIndicator inline />}
                {!isLoading && (
                  <>
                    <div className={styles.otaHolderInner}>
                      <OTAIcon className={styles.otaGuestsIcon} />
                      <span className={styles.otaGuestsReachedNumber}>
                        {formatNumberAsThousands(otaGuestsReached)}
                      </span>
                      <span className={styles.otaGuestsDesc}>OTA guests reached</span>
                    </div>
                    <div className={styles.otaGuestsNavigation}>
                      <Link to="/analytics/OTA-outreach">See more on OTA outreach →</Link>
                    </div>
                  </>
                )}
              </div>
              <div className={styles.textBlastHolder}>
                {isLoading && <LoadingIndicator inline />}
                {!isLoading && (
                  <>
                    <div className={styles.textBlastHolderInner}>
                      <h3 className={styles.title}>Drip campaign results</h3>
                      <div className={styles.textBlastHeader}>
                        <p className={styles.textBlastCountNumber}>
                          {textBlastData
                            ? formatNumberAsThousands(textBlastData?.total)
                            : 0}
                        </p>
                        <p className={styles.segmentsCountLabel}>
                          guests reached in total
                        </p>
                      </div>
                      <div>
                        {textBlastData?.activities.map(s => (
                          <div className={styles.textBlastCampaign}>
                            <div className={styles.textBlastCampaignHeader}>
                              <p className={styles.textBlastCampaignName}>{s.name}</p>
                              <p className={styles.textBlastCampaignDate}>
                                {moment(s.date).format('MMM DD, YYYY')}
                              </p>
                            </div>
                            <p className={styles.textBlastCampaignCount}>
                              {s.count}{' '}
                              <span className={styles.textBlastCampaignCountLabel}>
                                guests targeted
                              </span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.marketingMessagesHolder}>
              <h3 className={styles.title}>Marketing messages &amp; clicks</h3>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    width={500}
                    height={300}
                    data={messageActivities?.map(a => ({ ...a, messages: a.count }))}
                    margin={{
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 15
                    }}
                  >
                    <Legend verticalAlign="top" align="right" height={36} />

                    <CartesianGrid stroke="#F2F2F2" vertical={false} />
                    <XAxis
                      dataKey="date"
                      axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(0, 14)'
                      }}
                      tickFormatter={date => moment(date).format('MMM D')}
                      tickSize={0}
                      padding={{ top: 30, bottom: 20, right: 40 }}
                    />
                    <YAxis
                      tickSize={0}
                      yAxisId="left"
                      axisLine={false}
                      allowDecimals={false}
                      orientation="right"
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(-10, 0)'
                      }}
                      padding={{ top: 40, bottom: 0 }}
                    />
                    <YAxis
                      tickSize={0}
                      yAxisId="right"
                      axisLine={false}
                      allowDecimals={false}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(-10, 0)'
                      }}
                      padding={{ top: 40, bottom: 0 }}
                    />
                    {renderReferenceArea(restrictedDates)}
                    {renderYearSeperators(messageActivities)}
                    <Tooltip
                      content={
                        <InsightsTooltip
                          labelFormatter={date => moment(date).format('MMM D, YYYY')}
                        />
                      }
                      cursor={{
                        stroke: 'red',
                        strokeWidth: 0,
                        fill: '#dedede',
                        opacity: 0.22
                      }}
                    />
                    <Line
                      dataKey="messages"
                      stroke="#4079F5"
                      type="monotone"
                      yAxisId="left"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line
                      dataKey="clicks"
                      stroke="#66BB6A"
                      type="monotone"
                      yAxisId="right"
                      dot={false}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Outreach
